import React, { useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Popover, Row, Col, Input, Dropdown, Menu, Radio, Button, Tooltip, message } from 'antd';
import PropTypes from 'prop-types';
import copy from 'copy-to-clipboard';

import {
  AIBotIcon,
  BulbIcon,
  ExpandIcon,
  MoreIcon,
  DropdownIcon,
  CloseIcon,
  PlayIcon,
  TickIcon,
} from 'components/Icons';
import { AI_TONE_MENU, AI_LENGTH_MENU, AI_MORE_MENU } from 'constants/index';
import { RTL_LANGUAGES } from 'constants/languages';
import { OPEN_AI_RECOMMENDATIONS } from 'graphql/queries/proposalQueries';
import useWindowDimensions from 'hooks/useWindowDimensions';

const DEFAULT_AI_LENGTH_OPTIONS = AI_LENGTH_MENU.map((item) => item.label);

/**
 * Select options
 * @param {array} stateTypes - The state types
 * @param {function} setState - The function to set the state
 * @param {string} label - The label to select
 */
const selectOptions = (stateTypes, setState, label) => {
  const isSelected = stateTypes.includes(label);
  setState(isSelected ? stateTypes.filter((t) => t !== label) : [...stateTypes, label]);
};

/**
 * AITextHelper component
 * @param {object} props - The props
 * @param {React.ReactNode} props.children - The children
 * @param {string} props.value - The value of the input
 * @param {function} props.onInsert - The function to insert the text
 * @param {string} props.language - The language
 * @param {array} props.aiLengthMenu - The length menu options
 * @param {string} props.placeholder - The placeholder for the input
 * @param {string} props.inputType - The type of the input
 */
const AITextHelper = ({
  children,
  value,
  onInsert,
  language = 'english',
  aiLengthMenu = DEFAULT_AI_LENGTH_OPTIONS,
  placeholder = 'Ask AI to write text for...',
  inputType = 'textarea',
}) => {
  const { isMobile } = useWindowDimensions();
  const wrapperRef = useRef(null);
  const selectedTextRef = useRef('');
  const selectedRangeRef = useRef(null);
  const keepPopoverOpenRef = useRef(false);

  const [visible, setVisible] = useState(false);
  const [aiCommand, setAICommand] = useState('');
  const [selectedTones, setSelectedTones] = useState([]);
  const [selectedLength, setSelectedLength] = useState('');
  const [selectedMores, setSelectedMores] = useState([]);
  const [aiToolBarVisible, setAIToolBarVisible] = useState('');
  const [recommendationList, setRecommendationList] = useState([]);

  const languageRTL = RTL_LANGUAGES.includes(language?.toLowerCase());

  const [loadRecommendations, { loading: isFetchingRecommendations }] = useLazyQuery(
    OPEN_AI_RECOMMENDATIONS,
    {
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        setRecommendationList(data?.openAIRecommendations || []);
      },
    }
  );

  /**
   * Read and cache currently selected text from the wrapped input/textarea
   * so clicking the AI icon does not lose the user selection context.
   */
  const cacheSelectedText = () => {
    const wrapper = wrapperRef.current;
    if (!wrapper) {
      selectedTextRef.current = '';
      selectedRangeRef.current = null;
      return '';
    }

    const inputEl =
      document.activeElement && wrapper.contains(document.activeElement)
        ? document.activeElement
        : wrapper.querySelector('textarea, input');

    if (
      inputEl &&
      typeof inputEl.selectionStart === 'number' &&
      typeof inputEl.selectionEnd === 'number'
    ) {
      const start = inputEl.selectionStart;
      const end = inputEl.selectionEnd;
      const selectedText = inputEl.value?.slice(start, end) || '';

      selectedTextRef.current = selectedText.trim();
      selectedRangeRef.current = { inputEl, start, end };
      return selectedTextRef.current;
    }

    selectedTextRef.current = '';
    selectedRangeRef.current = null;
    return '';
  };

  const restoreSelection = () => {
    const selectedRange = selectedRangeRef.current;
    if (!selectedRange?.inputEl) {
      return;
    }

    const { inputEl, start, end } = selectedRange;
    if (typeof inputEl.focus === 'function' && typeof inputEl.setSelectionRange === 'function') {
      inputEl.focus();
      inputEl.setSelectionRange(start, end);
    }
  };

  const getDropdownContainer = (trigger) =>
    trigger?.closest?.('.ai-text-helper-popover') || trigger?.parentNode || document.body;

  const markInternalInteraction = () => {
    keepPopoverOpenRef.current = true;
    setTimeout(() => {
      keepPopoverOpenRef.current = false;
    }, 200);
  };

  /**
   * Fetch AI recommendations
   */
  const fetchAIRecommendation = () => {
    const prompt = aiCommand.trim() || selectedTextRef.current || value || '';
    if (!prompt) {
      message.warning('Please enter a prompt or select existing text');
      return;
    }
    setRecommendationList([]);
    loadRecommendations({
      variables: {
        prompt,
        tone: selectedTones.length ? new Intl.ListFormat().format(selectedTones) : undefined,
        recommendationSize:
          selectedLength?.key !== undefined ? aiLengthMenu[selectedLength.key] : undefined,
        command: aiCommand.trim() || undefined,
        enhance: selectedMores.includes('Enhanced Writing'),
        grammar: selectedMores.includes('Grammar Check'),
        language: language || 'english',
      },
    });
  };

  /**
   * Handle insert of AI recommendation
   * @param {string} text - The text to insert
   */
  const handleInsert = (text) => {
    onInsert?.(text);
    setVisible(false);
    setRecommendationList([]);
    setAICommand('');
    setSelectedTones([]);
    setSelectedLength('');
    setSelectedMores([]);
  };

  /**
   * Copy recommendation to clipboard
   * @param {string} recommendation - The recommendation to copy
   */
  const copyRecommendation = (recommendation) => {
    copy(recommendation);
    message.success('Copied to Clipboard');
  };

  /**
   * Handle visible change
   * @param {boolean} newVisible - The new visible state
   */
  const handleVisibleChange = (newVisible) => {
    if (
      !newVisible &&
      (keepPopoverOpenRef.current ||
        !!document.querySelector('.ai-toolbar-dropdown.ant-dropdown:not(.ant-dropdown-hidden)'))
    ) {
      return;
    }

    if (newVisible) {
      cacheSelectedText();
      restoreSelection();
    }

    if (!newVisible) {
      setRecommendationList([]);
      setAICommand('');
      setAIToolBarVisible('');
    }
    setVisible(newVisible);
  };

  /**
   * Check if there is a prompt
   * @returns {boolean} - True if there is a prompt, false otherwise
   */
  const hasPrompt = aiCommand?.trim() || selectedTextRef.current || value;
  const generateButtonDisabled = !hasPrompt;

  /**
   * Tone menu
   * @returns {React.ReactNode} - The tone menu
   */
  const toneMenu = (
    <Menu
      className={`ai-toolbar-dropdown-menu${recommendationList.length ? '-list' : ''}`}
      multiple
      onMouseDown={markInternalInteraction}
      selectedKeys={AI_TONE_MENU.filter((m) => selectedTones.includes(m.label)).map((m) => m.key)}
      onClick={({ key }) => {
        markInternalInteraction();
        const item = AI_TONE_MENU.find((m) => m.key === key);
        if (item) selectOptions(selectedTones, setSelectedTones, item.label);
        setAIToolBarVisible('');
        setTimeout(restoreSelection, 0);
      }}>
      {AI_TONE_MENU.map((aiToneMenuItem) => (
        <Menu.Item key={aiToneMenuItem.key}>
          {aiToneMenuItem.icon}
          {aiToneMenuItem.label}
          {selectedTones.includes(aiToneMenuItem.label) ? (
            <TickIcon className="ai-toolbar-dropdown-menu-check-icon" />
          ) : null}
        </Menu.Item>
      ))}
    </Menu>
  );

  /**
   * Length menu
   * @returns {React.ReactNode} - The length menu
   */
  const lengthMenu = (
    <Menu
      className={`ai-toolbar-dropdown-menu${recommendationList.length ? '-list' : ''}`}
      onMouseDown={markInternalInteraction}
      onClick={({ key }) => {
        markInternalInteraction();
        setSelectedLength(AI_LENGTH_MENU[key]);
        setAIToolBarVisible('');
        setTimeout(restoreSelection, 0);
      }}>
      {AI_LENGTH_MENU.map((aiLengthMenuItem, idx) => (
        <Menu.Item key={aiLengthMenuItem.key}>
          <Radio checked={selectedLength?.key === aiLengthMenuItem.key} />
          <span>{aiLengthMenu[idx] ?? aiLengthMenuItem.label}</span>
        </Menu.Item>
      ))}
    </Menu>
  );

  /**
   * More menu
   * @returns {React.ReactNode} - The more menu
   */
  const moreMenu = (
    <Menu
      className={`ai-toolbar-dropdown-menu${
        recommendationList.length ? '-list' : ''
      } ai-toolbar-dropdown-menu-more`}
      multiple
      onMouseDown={markInternalInteraction}
      selectedKeys={AI_MORE_MENU.filter((m) => selectedMores.includes(m.label)).map((m) => m.key)}
      onClick={({ key }) => {
        markInternalInteraction();
        const item = AI_MORE_MENU.find((m) => m.key === key);
        if (item) selectOptions(selectedMores, setSelectedMores, item.label);
        setAIToolBarVisible('');
        setTimeout(restoreSelection, 0);
      }}>
      {AI_MORE_MENU.map((aiMoreMenuItem) => (
        <Menu.Item key={aiMoreMenuItem.key}>
          {aiMoreMenuItem.label}
          {selectedMores.includes(aiMoreMenuItem.label) ? <TickIcon /> : null}
          {aiMoreMenuItem.icon}
        </Menu.Item>
      ))}
    </Menu>
  );

  /**
   * Popover content
   * @returns {React.ReactNode} - The popover content
   */
  const popoverContent = (
    <div
      className="ai-text-helper-popover"
      onMouseDown={markInternalInteraction}
      onClick={markInternalInteraction}>
      <div className="ai-toolbar ai-toolbar-ask-ai">
        <Col className="ai-toolbar-initial">
          <Row className={isFetchingRecommendations ? 'ai-toolbar-input-ask-ai' : ''}>
            <Input.Group className="ai-toolbar-input-group-ask-ai">
              <Col>
                <AIBotIcon />
              </Col>
              <Col>
                <Input.TextArea
                  className="ai-toolbar-input-text-area"
                  placeholder={placeholder}
                  onChange={(e) => setAICommand(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      fetchAIRecommendation();
                    }
                  }}
                  value={aiCommand}
                  autoSize={{ minRows: 1, maxRows: 1 }}
                />
              </Col>
              <Col>
                <Tooltip title="Generate">
                  <span
                    onMouseDown={(e) => {
                      markInternalInteraction();
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={
                      !generateButtonDisabled
                        ? () => {
                            markInternalInteraction();
                            fetchAIRecommendation();
                            setTimeout(restoreSelection, 0);
                          }
                        : undefined
                    }
                    style={{ cursor: generateButtonDisabled ? 'not-allowed' : 'pointer' }}
                    disabled={generateButtonDisabled}>
                    <PlayIcon
                      className={generateButtonDisabled ? 'disabled' : ''}
                      style={{ color: 'rgba(0,0,0,.45)' }}
                    />
                  </span>
                </Tooltip>
              </Col>
            </Input.Group>
          </Row>
          <Row className="ai-toolbar-options">
            <Row justify="space-between" wrap={false} className="ai-toolbar-options-row">
              <Col>
                <Dropdown
                  overlay={toneMenu}
                  overlayClassName="ai-toolbar-dropdown"
                  getPopupContainer={getDropdownContainer}
                  placement={languageRTL ? 'bottomRight' : 'bottomLeft'}
                  trigger={['click']}
                  visible={aiToolBarVisible === 'tones'}
                  onVisibleChange={(v) => {
                    markInternalInteraction();
                    setAIToolBarVisible(v ? 'tones' : '');
                  }}>
                  <div
                    className="ai-toolbar-dropdown-placeholder"
                    onMouseDown={(e) => {
                      markInternalInteraction();
                      e.preventDefault();
                    }}>
                    <BulbIcon />
                    <Row className="ai-toolbar-icon-text">
                      <Col>Tone&nbsp;</Col>
                      {selectedTones.length ? (
                        <Col>
                          <span>({selectedTones.length})</span>
                          <CloseIcon
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTones([]);
                            }}
                          />
                        </Col>
                      ) : null}
                    </Row>
                    <DropdownIcon className="ai-toolbar-dropdown-icon" />
                  </div>
                </Dropdown>
              </Col>
              <Col>
                <Dropdown
                  overlay={lengthMenu}
                  overlayClassName="ai-toolbar-dropdown"
                  getPopupContainer={getDropdownContainer}
                  placement={languageRTL ? 'bottomRight' : 'bottomLeft'}
                  trigger={['click']}
                  visible={aiToolBarVisible === 'lengths'}
                  onVisibleChange={(v) => {
                    markInternalInteraction();
                    setAIToolBarVisible(v ? 'lengths' : '');
                  }}>
                  <div
                    className="ai-toolbar-dropdown-placeholder"
                    onMouseDown={(e) => {
                      markInternalInteraction();
                      e.preventDefault();
                    }}>
                    <ExpandIcon />
                    <Row className="ai-toolbar-icon-text">
                      <Col>Length&nbsp;</Col>
                      {selectedLength ? (
                        <Col>
                          <CloseIcon
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedLength('');
                            }}
                          />
                        </Col>
                      ) : null}
                    </Row>
                    <DropdownIcon className="ai-toolbar-dropdown-icon" />
                  </div>
                </Dropdown>
              </Col>
              <Col>
                <Dropdown
                  overlay={moreMenu}
                  overlayClassName="ai-toolbar-dropdown ai-toolbar-dropdown-menu-more"
                  getPopupContainer={getDropdownContainer}
                  placement={languageRTL ? 'bottomRight' : 'bottomLeft'}
                  trigger={['click']}
                  visible={aiToolBarVisible === 'mores'}
                  onVisibleChange={(v) => {
                    markInternalInteraction();
                    setAIToolBarVisible(v ? 'mores' : '');
                  }}>
                  <div
                    className="ai-toolbar-dropdown-placeholder"
                    onMouseDown={(e) => {
                      markInternalInteraction();
                      e.preventDefault();
                    }}>
                    <MoreIcon />
                    <Row className="ai-toolbar-icon-text">
                      <Col>More&nbsp;</Col>
                      {selectedMores.length ? (
                        <Col>
                          <span>({selectedMores.length})</span>
                          <CloseIcon
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedMores([]);
                            }}
                          />
                        </Col>
                      ) : null}
                    </Row>
                    <DropdownIcon className="ai-toolbar-dropdown-icon" />
                  </div>
                </Dropdown>
              </Col>
            </Row>
          </Row>
          {isFetchingRecommendations ? (
            <Row justify="space-between" className="ai-toolbar-generating">
              <Row align="middle">
                <AIBotIcon />
                <Row className="ai-toolbar-icon-text"> AI is writing...</Row>
              </Row>
            </Row>
          ) : null}
          {recommendationList.length ? (
            <Col className="ai-toolbar-suggestion-container">
              <Row justify="space-between" className="ai-toolbar-suggestion-header">
                <Row align="middle">
                  <Col className="ai-toolbar-icon-text">Suggestions</Col>
                </Row>
              </Row>
              <Row className="ai-toolbar-suggestion-list">
                {recommendationList.map((item, idx) => (
                  <Row key={idx} className="ai-toolbar-suggestion-body">
                    <Row className="ai-toolbar-suggestion-content">{item.text}</Row>
                    <Row justify="space-between" className="ai-toolbar-suggestion-footer">
                      <Row align="middle">{selectedTones?.join(', ')}</Row>
                      <Row align="middle" className="ai-toolbar-suggestion-options">
                        <Button size="small" onClick={() => copyRecommendation(item.text)}>
                          COPY
                        </Button>
                        <Button size="small" type="primary" onClick={() => handleInsert(item.text)}>
                          INSERT
                        </Button>
                      </Row>
                    </Row>
                  </Row>
                ))}
              </Row>
            </Col>
          ) : null}
        </Col>
      </div>
    </div>
  );

  /**
   * Render the AITextHelper
   * @returns {React.ReactNode} - The AITextHelper
   */
  return (
    <div className={`ai-text-helper-wrapper ai-text-helper-${inputType} `} ref={wrapperRef}>
      {children}
      <Popover
        content={popoverContent}
        trigger="click"
        visible={visible}
        onVisibleChange={handleVisibleChange}
        placement={isMobile ? 'bottom' : 'bottomRight'}
        overlayClassName={`ai-text-helper-popover-overlay ${
          isMobile ? 'ai-text-helper-mobile' : ''
        } ${languageRTL ? 'ai-text-helper-rtl' : ''}`}
        getPopupContainer={isMobile ? () => document.body : (trigger) => trigger.parentNode}>
        <Tooltip overlayClassName="ai-text-helper-tooltip" title="Write with AI">
          <span
            className="ai-text-helper-icon"
            onMouseDown={(e) => {
              // Keep focus/selection on the source input while opening AI.
              cacheSelectedText();
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              cacheSelectedText();
              e.stopPropagation();
            }}>
            <AIBotIcon />
          </span>
        </Tooltip>
      </Popover>
    </div>
  );
};

/**
 * Prop types
 * @returns {object} - The prop types
 */
AITextHelper.propTypes = {
  children: PropTypes.node.isRequired,
  value: PropTypes.string,
  onInsert: PropTypes.func.isRequired,
  language: PropTypes.string,
  aiLengthMenu: PropTypes.arrayOf(PropTypes.string),
  placeholder: PropTypes.string,
  inputType: PropTypes.oneOf(['input', 'textarea']),
};

/**
 * Default props
 * @returns {object} - The default props
 */
AITextHelper.defaultProps = {
  value: '',
  language: 'english',
  aiLengthMenu: DEFAULT_AI_LENGTH_OPTIONS,
  placeholder: 'Ask AI to write text for...',
  inputType: 'textarea',
};

/**
 * Export the AITextHelper
 * @returns {React.Component} - The AITextHelper
 */
export default AITextHelper;
